import type {
  ColumnDef,
  DataType,
  BadgeVariant,
} from '@/components/custom/datatable/types'
import type { CellContext } from '@tanstack/react-table'
import type { ReactNode } from 'react'

interface ColumnConfig<T> {
  id: string
  header: string
  accessorFn: (row: T) => unknown
  width?: number
  isFilterable?: boolean
  isSortable?: boolean
  dataType?: DataType
  displayOrder?: number
  options?: string[]
  badgeVariant?: BadgeVariant
  displayProperty?: string
  format?: string
  cell?: (info: CellContext<T, unknown>) => ReactNode
}

/**
 * Create a column definition with sensible defaults.
 * Reduces boilerplate when defining DataTable columns manually.
 *
 * @example
 * const columns: ColumnDef<Contact>[] = [
 *   createColumn({
 *     id: 'name',
 *     header: 'Name',
 *     accessorFn: (row) => row.name,
 *     width: 200,
 *     isFilterable: true,
 *     isSortable: true,
 *   }),
 * ]
 */
export function createColumn<T>({
  id,
  header,
  accessorFn,
  width = 150,
  isFilterable = false,
  isSortable = false,
  dataType = 'string',
  displayOrder = 0,
  options,
  badgeVariant,
  displayProperty,
  format,
  cell,
}: ColumnConfig<T>): ColumnDef<T> {
  return {
    id,
    accessorFn,
    header,
    fieldName: id,
    displayName: header,
    dataType,
    isFilterable,
    isSortable,
    isEditable: false,
    width,
    displayOrder,
    isVisible: true,
    options,
    meta: {
      fieldName: id,
      displayName: header,
      dataType,
      isFilterable,
      isSortable,
      isEditable: false,
      options,
      badgeVariant,
      displayProperty,
      format,
    },
    cell,
  }
}
